import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import ContactForm from '../components/ContactForm';
import StarField from '../components/StarField';
import bgSrc from '../artifacts/robs-HOrhCnQsxnQ-unsplash.jpg';
import { useMediaQuery } from 'react-responsive';

const Contact = () => {
    const isNarrowWidth = useMediaQuery({ query: '(max-width: 900px)' });
    React.useEffect(() => { document.title = 'Gripfire, Inc. - Contact' }, []);

    return (
        <Box sx={rootSx}>
            <StarField />
            <Box sx={topSpacerSx} />
            <Box sx={{ ...wrapperSx, ...(isNarrowWidth ? { flexDirection: 'column', alignItems: 'center' } : {}) }}>
                <Box sx={leftColumnSx}>
                    <Typography sx={eyebrowSx}>Let&apos;s build what matters</Typography>
                    <Typography variant="h2" sx={titleSx}>
                        Tell us what you&apos;re trying to build, fix, or modernize.
                    </Typography>

                    <Stack spacing={2}>
                        <Typography variant="h6" sx={descrSx}>
                            Gripfire is a U.S.-based software engineering company with locations in Maryland and
                            Pennsylvania. We support specialized applications, data systems, and operational workflows
                            for teams that need practical technical traction.
                        </Typography>

                        <Typography variant="h6" sx={descrSx}>
                            Share the mission, the constraint, or the messy workflow. We&apos;ll help identify the next useful step —
                            including where AI-assisted research, analysis, or automation can responsibly shorten the path to a solution.
                        </Typography>
                    </Stack>

                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={chipRowSx}>
                        <Chip label="Custom software" sx={chipSx} />
                        <Chip label="Modernization" sx={chipSx} />
                        <Chip label="Data systems" sx={chipSx} />
                    </Stack>
                </Box>
                <Box sx={formColumnSx}>
                    <ContactForm />
                </Box>
            </Box>
            <Box sx={bottomSpacerSx} />
        </Box>
    );
}

export default Contact;

const rootSx = {
    width: '100%',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: 0,
    boxSizing: 'border-box',
    position: 'relative',
    overflowX: 'clip',
    '&::before': {
        opacity: '.14',
        backgroundImage: 'url(' + bgSrc + ')',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        width: '100%',
        height: '100%',
        top: 0,
        left: 0,
        position: 'absolute',
        content: '""'
    },
    '&::after': {
        content: '""',
        position: 'absolute',
        inset: 0,
        background: 'radial-gradient(circle at 70% 25%, rgba(255, 103, 35, .18), transparent 28%), linear-gradient(180deg, rgba(8,8,8,.86), rgba(8,8,8,.96))'
    }
};

const topSpacerSx = {
    position: 'relative',
    zIndex: 1,
    flex: '0 0 auto',
    height: { xs: 'calc(5.75rem + env(safe-area-inset-top))', sm: '8rem' }
};

const bottomSpacerSx = {
    position: 'relative',
    zIndex: 1,
    flex: '0 0 auto',
    height: { xs: 'calc(16rem + env(safe-area-inset-bottom))', sm: '8rem' },
    '@media (orientation: landscape) and (max-height: 500px)': {
        height: '10rem'
    }
};

const wrapperSx = {
    position: 'relative',
    zIndex: 1,
    width: 'min(1120px, calc(100% - 1.5rem))',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    gap: { xs: '2rem', sm: '3rem' }
};

const eyebrowSx = {
    mb: 2,
    color: '#ff9b66',
    fontSize: '.78rem',
    fontWeight: 800,
    lineHeight: 1.4,
    letterSpacing: { xs: '.1em', sm: '.18em' },
    textTransform: 'uppercase'
};

const titleSx = {
    pb: 2,
    fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
    fontWeight: 800,
    fontSize: { xs: '2.15rem', sm: '3.75rem' },
    letterSpacing: { xs: '-.03em', sm: '-.045em' },
    lineHeight: 1.04
};

const descrSx = {
    color: 'rgba(255,255,255,.72)',
    lineHeight: 1.7
};

const leftColumnSx = {
    p: 0,
    maxWidth: '520px'
};

const formColumnSx = {
    width: '100%',
    maxWidth: '520px',
    pb: { xs: 'calc(5rem + env(safe-area-inset-bottom))', sm: '4rem' },
    '@media (max-height: 650px)': {
        pb: '8rem'
    },
    '@media (orientation: landscape) and (max-height: 500px)': {
        pb: '10rem'
    }
};

const chipRowSx = {
    mt: 3
};

const chipSx = {
    color: '#fff',
    border: '1px solid rgba(255, 140, 82, .45)',
    background: 'rgba(255,255,255,.06)'
};
